import React from 'react'

// Constants
import { countries } from '@sketch/constants'

// Utils
import {
  confirmPendingSetupIntent,
  isPaymentMethodCard,
  isPaymentMethodInvoice,
  isWorkspaceSubscriptionActive,
} from 'modules/workspace/utils'
import { ErrorHandler } from '@sketch/tracing'

// Components
import Panel from '../Panel'
import { PaymentLogo } from '@sketch/components'
import { useModalContext } from 'containers/ModalManager/ModalContext'
import { useStripe } from 'containers/StripeProvider'

// Local Components
import BillingInfoTable from '../BillingInfoTable'
import EditAddressDetailsModal from 'modules/workspace/modals/EditAddressDetailsModal'
import EditPaymentMethodModal from 'modules/workspace/modals/EditPaymentMethodModal'
import { VatStatus } from 'modules/workspace/components/VatStatus'

// Styles
import {
  PaymentInformationWrapper,
  List,
  StyledButton,
  StyledAsyncButton,
  PaymentLogoWrapper,
  SCAWarningPill,
  CreditBalanceTextContainer,
  CreditLogoAmountContainer,
  PaymentInformationTitleContainer,
  StripeSecureInfo,
  LockIcon,
} from './PaymentInformationPanel.styles'

// Types
import {
  BillingDetailsFragment,
  PaymentDetailsFragment,
} from '@sketch/gql-types'

import { formatPriceShort } from '@sketch/utils'

// This is a valid use case of expansive types
// eslint-disable-next-line no-restricted-imports
import { BillingStatus } from '@sketch/gql-types/expansive'

const IGNORE_UI_REFRESH_STATUS = ['ACTIVE', 'TRIAL']

type Countries = keyof typeof countries

interface PaymentInformationPanelProps {
  customerId: string
  credits: number
  billingDetails: BillingDetailsFragment
  paymentDetails?: PaymentDetailsFragment
  subscriptionStatus: BillingStatus
  pendingSCAToken?: string
  onWorkspaceSettingRefresh: () => Promise<void>
}

/*
 * PaymentInformationPanel
 *
 * Renders the Payments Information panel inside the Billing page
 */
export const PaymentInformationPanel: React.FC<PaymentInformationPanelProps> = props => {
  const {
    customerId,
    billingDetails,
    paymentDetails,
    credits,
    subscriptionStatus,
    pendingSCAToken,
    onWorkspaceSettingRefresh,
  } = props
  const { showModal } = useModalContext()
  const { stripe } = useStripe()

  // Billing details
  const { name, email, address, taxId } = billingDetails

  const billingItems: (string | null)[] = [name, email]

  if (address) {
    const stateAndPostalCode = [address.state, address.postalCode].join(' ')
    const country = countries[address.country as Countries] || address.country

    billingItems.push(
      null, // A blank line separating email from address
      address?.line1,
      address?.city,
      stateAndPostalCode,
      country
    )
  }

  const onPaymentUpdated = async () => {
    /**
     * We need to refetch most of the workspace settings information
     * because when updating payment method you can now reactivate
     * a old workspace with a canceled subscription or validate a card
     * that had a pending 3D secure challenge.
     *
     * With these interactions the data returned by the API might be
     * different in a lot of parameters like the number of seats or the
     * plan renewal date in order, and instead of manually updating the
     * cache with this values we opted by refetching the queries again
     * preventing error prone information
     */

    if (IGNORE_UI_REFRESH_STATUS.includes(subscriptionStatus)) {
      /**
       * We can safely ignore these status because most of the workspaces
       * information won't change
       */
      return
    }

    await onWorkspaceSettingRefresh()
  }

  // Prepare the UI for each Payment method type
  let paymentContent = null

  if (
    paymentDetails &&
    isPaymentMethodCard(paymentDetails) &&
    pendingSCAToken
  ) {
    const { last4Digits, brand, expiration } = paymentDetails
    paymentContent = (
      <>
        <BillingInfoTable.Cell>
          <PaymentLogoWrapper>
            <PaymentLogo brand={brand} />
            &nbsp; ···· {last4Digits} · Expires {expiration.month}/
            {String(expiration.year).slice(-2)}
          </PaymentLogoWrapper>
          <SCAWarningPill color="orange" variant={{ kind: 'primary' }}>
            3D Secure Authentication Needed
          </SCAWarningPill>
        </BillingInfoTable.Cell>
        <BillingInfoTable.Cell>
          <StyledAsyncButton
            size="40"
            data-test="sca-verify-button"
            onClick={async () => {
              if (!stripe) {
                return
              }

              await confirmPendingSetupIntent(stripe, pendingSCAToken)
              await onPaymentUpdated()
            }}
          >
            Verify&hellip;
          </StyledAsyncButton>
          <StyledButton
            size="40"
            data-test="credit-card-button"
            onClick={() =>
              showModal(EditPaymentMethodModal, {
                customerId,
                onPaymentUpdated,
                isWorkspaceSubscriptionActive: isWorkspaceSubscriptionActive(
                  subscriptionStatus
                ),
              })
            }
          >
            Edit&hellip;
          </StyledButton>
        </BillingInfoTable.Cell>
      </>
    )
  } else if (paymentDetails && isPaymentMethodCard(paymentDetails)) {
    const { last4Digits, brand, expiration } = paymentDetails
    paymentContent = (
      <>
        <BillingInfoTable.Cell>
          <PaymentLogoWrapper>
            <PaymentLogo brand={brand} />
            &nbsp; ···· {last4Digits} · Expires {expiration.month}/
            {String(expiration.year).slice(-2)}
          </PaymentLogoWrapper>
        </BillingInfoTable.Cell>
        <BillingInfoTable.Cell>
          <StyledButton
            size="32"
            data-test="credit-card-button"
            onClick={() =>
              showModal(EditPaymentMethodModal, {
                customerId,
                onPaymentUpdated,
                isWorkspaceSubscriptionActive: isWorkspaceSubscriptionActive(
                  subscriptionStatus
                ),
              })
            }
          >
            Edit&hellip;
          </StyledButton>
        </BillingInfoTable.Cell>
      </>
    )
  } else if (paymentDetails && isPaymentMethodInvoice(paymentDetails)) {
    paymentContent = (
      <BillingInfoTable.Cell>
        <PaymentLogoWrapper>
          <PaymentLogo brand="invoice" /> &nbsp;&nbsp;Invoice Payment
        </PaymentLogoWrapper>
      </BillingInfoTable.Cell>
    )
  }

  return (
    <PaymentInformationWrapper>
      <Panel.Title>
        <PaymentInformationTitleContainer>
          <div>Payment Details</div>
          <StripeSecureInfo>
            All transactions are secure and encrypted by&nbsp;
            <a
              target="_blank"
              href="https://stripe.com"
              rel="noopener noreferrer"
            >
              Stripe
            </a>
            <LockIcon />
          </StripeSecureInfo>
        </PaymentInformationTitleContainer>
      </Panel.Title>

      <Panel.Body>
        {credits !== 0 && (
          <BillingInfoTable.Row>
            <BillingInfoTable.HeadingCell>
              Credit Balance
            </BillingInfoTable.HeadingCell>
            <BillingInfoTable.Cell>
              <CreditBalanceTextContainer>
                <CreditLogoAmountContainer>
                  <PaymentLogo brand="credit" />
                  {formatPriceShort(credits)}
                </CreditLogoAmountContainer>
                <div>
                  We’ll always use your available credit balance before charging
                  your card.
                </div>
              </CreditBalanceTextContainer>
            </BillingInfoTable.Cell>
          </BillingInfoTable.Row>
        )}
        <BillingInfoTable>
          {paymentContent && (
            <BillingInfoTable.Row alignItems="center">
              <BillingInfoTable.HeadingCell>
                Payment Method
              </BillingInfoTable.HeadingCell>
              {paymentContent}
            </BillingInfoTable.Row>
          )}

          {paymentDetails && paymentDetails.type !== 'NONE' && (
            <BillingInfoTable.Row>
              <BillingInfoTable.HeadingCell>
                Billing Details
              </BillingInfoTable.HeadingCell>
              <BillingInfoTable.Cell>
                <List>
                  {billingItems.map((item, index) => (
                    <li key={index}>{item || <>&nbsp;</>}</li>
                  ))}
                  {taxId && <VatStatus taxId={taxId} />}
                </List>
              </BillingInfoTable.Cell>
              {isWorkspaceSubscriptionActive(subscriptionStatus) &&
                paymentDetails &&
                isPaymentMethodCard(paymentDetails) && (
                  <BillingInfoTable.Cell>
                    <StyledButton
                      size="32"
                      data-test="billing-address-button"
                      onClick={() => {
                        if (billingDetails.address) {
                          const { address } = billingDetails

                          showModal(EditAddressDetailsModal, {
                            customerId,
                            // Force the type to be checked correctly
                            billingDetails: {
                              ...billingDetails,
                              address,
                            },
                          })
                        } else {
                          ErrorHandler.shouldNeverHappen(
                            'A Workspace with a payment method should always have a Billing address'
                          )
                        }
                      }}
                    >
                      Edit&hellip;
                    </StyledButton>
                  </BillingInfoTable.Cell>
                )}
            </BillingInfoTable.Row>
          )}
        </BillingInfoTable>
      </Panel.Body>
    </PaymentInformationWrapper>
  )
}
